import React, { useState, useRef, useEffect } from 'react';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const MagicCodeSignIn = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'verifying'
  const [email, setEmail] = useState('');
  const [codeValue, setCodeValue] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const hiddenInputRef = useRef(null);

  // Countdown for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Auto-focus hidden input when entering code step
  useEffect(() => {
    if (step === 'code' && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  }, [step]);

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (!email || sending) return;
    setError(null);
    setSending(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/magic/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await res.clone().json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send code');
      }

      setStep('code');
      setResendTimer(60);
      setCodeValue('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCodeValue(digits);
    setError(null);

    // Auto-submit when all 6 digits entered
    if (digits.length === 6) {
      handleVerifyCode(digits);
    }
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Backspace' && codeValue.length === 0) {
      // Nothing to delete
    }
  };

  const focusHiddenInput = () => {
    if (step === 'code' && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  };

  const handleVerifyCode = async (codeStr) => {
    setStep('verifying');
    setError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/magic/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: codeStr,
        }),
      });

      const data = await res.clone().json();

      if (data.success === false) {
        setStep('code');
        throw new Error(data.detail || 'Invalid code. Please try again.');
      }

      // Save session
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      localStorage.setItem('user_data', JSON.stringify({
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        picture: data.picture,
      }));

      // Apply pending referral
      const pendingReferral = localStorage.getItem('pending_referral_code');
      if (pendingReferral && data.session_token) {
        try {
          await fetch(`${BACKEND_URL}/api/billing/referral/apply`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.session_token}`,
            },
            credentials: 'include',
            body: JSON.stringify({ code: pendingReferral }),
          });
        } catch { /* silent */ }
        localStorage.removeItem('pending_referral_code');
      }

      if (onSuccess) onSuccess(data);
      else window.location.href = '/stash';

    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="w-full" data-testid="magic-code-signin">
      {step === 'email' && (
        <form onSubmit={handleSendCode}>
          <div className="space-y-3">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-soft" />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                placeholder="Enter your email"
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-beige bg-white text-sm text-charcoal placeholder:text-gray-light focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage transition-all"
                data-testid="magic-email-input"
                autoComplete="email"
                required
              />
            </div>
            {error && (
              <p className="text-xs text-red-500" data-testid="magic-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={!email || sending}
              className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                email && !sending
                  ? 'bg-charcoal text-white hover:bg-charcoal/90'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="magic-send-btn"
            >
              {sending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  Sending code...
                </span>
              ) : (
                'Send me a code'
              )}
            </button>
          </div>
        </form>
      )}

      {(step === 'code' || step === 'verifying') && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-charcoal/80">
              We sent a 6-digit code to
            </p>
            <p className="text-sm font-medium text-charcoal">{email}</p>
          </div>

          {/* 6-digit code display with hidden input for auto-fill */}
          <div
            className="flex justify-center gap-2 cursor-text"
            onClick={focusHiddenInput}
            data-testid="magic-code-boxes"
          >
            {/* Hidden input that captures auto-fill and keyboard input */}
            <input
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codeValue}
              onChange={handleCodeChange}
              onKeyDown={handleCodeKeyDown}
              disabled={step === 'verifying'}
              className="sr-only"
              data-testid="magic-code-hidden-input"
            />
            {/* Visual boxes */}
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`w-10 h-12 flex items-center justify-center text-lg font-mono font-semibold rounded-lg border transition-all duration-200 ${
                  step === 'verifying'
                    ? 'border-sage bg-sage/5 text-sage'
                    : error
                      ? 'border-red-300 bg-red-50 text-red-600'
                      : codeValue.length === i
                        ? 'border-sage bg-white ring-2 ring-sage/30'
                        : 'border-beige bg-white text-charcoal'
                }`}
                data-testid={`magic-code-box-${i}`}
              >
                {codeValue[i] || ''}
              </div>
            ))}
          </div>

          {step === 'verifying' && (
            <div className="flex items-center justify-center gap-2 text-sm text-sage">
              <Loader2 size={14} className="animate-spin" />
              Verifying...
            </div>
          )}

          {error && (
            <p className="text-xs text-red-500 text-center" data-testid="magic-error">{error}</p>
          )}

          <div className="flex items-center justify-between text-xs text-gray-soft">
            <button
              onClick={() => { setStep('email'); setError(null); setCodeValue(''); }}
              className="flex items-center gap-1 hover:text-charcoal transition-colors"
              data-testid="magic-back-btn"
            >
              <ArrowLeft size={12} /> Change email
            </button>
            <button
              onClick={handleSendCode}
              disabled={resendTimer > 0 || sending}
              className={resendTimer > 0 ? 'text-gray-light' : 'hover:text-charcoal transition-colors'}
              data-testid="magic-resend-btn"
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagicCodeSignIn;
